import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Drawer,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";
import { useNavigate, useLocation } from "react-router-dom";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAuth } from "../../../context/AuthContext";
import logo from "../../../static/media/logo.webp";

export default function Sidebar({ open, onClose }) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const { dashboardTheme } = useDashboardTheme();
  const { user } = useAuth();

  const [hoverOpen, setHoverOpen] = useState(false);
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  const permissions = user?.permissions || [];

  const canViewEnterprise =
    user?.role === "super_admin" ||
    user?.role === "admin" ||
    permissions.includes("enterprise.view");

  const canViewProducts =
    user?.role === "super_admin" ||
    user?.role === "admin" ||
    permissions.includes("products.view");

  const canViewPersons =
    user?.role === "super_admin" ||
    user?.role === "admin" ||
    permissions.includes("persons.view");

  const canViewVentas =
    user?.role === "super_admin" ||
    user?.role === "admin" ||
    permissions.includes("ventas.view");

  const expanded = isMobile ? true : open || hoverOpen;
  const sidebarWidth = expanded ? 260 : 82;

  const searchParams = new URLSearchParams(location.search);
  const currentSection = searchParams.get("section");

  const isProductsRoute = location.pathname.startsWith("/admin-panel/productos");
  const isPeopleRoute = location.pathname.startsWith("/admin-panel/personas");
  const isOrdersRoute = location.pathname.startsWith("/admin-panel/ordenes");
  const isDocumentsRoute = location.pathname.startsWith("/admin-panel/documentos");
  const isInventoryRoute = isProductsRoute;
  const isConfigRoute = location.pathname === "/admin-panel/configuracion";
  const isAppearanceActive = isConfigRoute && currentSection === "appearance";
  const isAccessActive = isConfigRoute && currentSection === "access";

  useEffect(() => {
    if (isConfigRoute && expanded) {
      setConfigOpen(true);
    }
  }, [isConfigRoute, expanded]);

  useEffect(() => {
    if (isInventoryRoute && expanded) {
      setInventoryOpen(true);
    }
  }, [isInventoryRoute, expanded]);

  const closeMobile = () => {
    if (isMobile) {
      onClose();
    }
  };

  const goRoute = (path) => {
    navigate(path);
    closeMobile();
  };

  const goTo = (section) => {
    navigate(`/admin-panel/configuracion?section=${section}`);
    closeMobile();
  };

  const handleInventoryClick = () => {
    if (!expanded) {
      setHoverOpen(true);
      setInventoryOpen(true);
      return;
    }

    setInventoryOpen((prev) => !prev);
  };

  const handleConfigClick = () => {
    if (!expanded) {
      setHoverOpen(true);
      setConfigOpen(true);
      return;
    }

    setConfigOpen((prev) => !prev);
  };

  const mainButtonStyles = (active) => ({
    width: "100%",
    minHeight: expanded ? 44 : 64,
    px: expanded ? 1.8 : 0.5,
    borderRadius: "14px",
    textTransform: "none",
    fontSize: 14,
    fontWeight: 600,
    bgcolor: active ? "rgba(255,255,255,0.14)" : "transparent",
    color: dashboardTheme.sidebar_text_color,
    boxShadow: "none",
    display: "flex",
    flexDirection: expanded ? "row" : "column",
    justifyContent: expanded ? "flex-start" : "center",
    alignItems: "center",
    gap: expanded ? 1 : 0.4,
    "& .MuiButton-startIcon": {
      m: 0,
      color: dashboardTheme.sidebar_text_color,
    },
    "&:hover": {
      bgcolor: "rgba(255,255,255,0.1)",
      boxShadow: "none",
    },
  });

  const nestedButtonStyles = (active) => ({
    width: "100%",
    justifyContent: "flex-start",
    minHeight: 38,
    px: 1.8,
    pl: 4.8,
    borderRadius: "12px",
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    bgcolor: active ? "rgba(255,255,255,0.14)" : "transparent",
    color: dashboardTheme.sidebar_text_color,
    boxShadow: "none",
    "&:hover": {
      bgcolor: "rgba(255,255,255,0.1)",
      boxShadow: "none",
    },
  });

  const shortTextStyles = {
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.1,
    maxWidth: 70,
    whiteSpace: "normal",
    textAlign: "center",
  };

  const content = (
    <Box
      onMouseEnter={() => {
        if (!isMobile && !open) {
          setHoverOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (!isMobile && !open) {
          setHoverOpen(false);
        }
      }}
      sx={{
        width: sidebarWidth,
        height: "100dvh",
        bgcolor: dashboardTheme.sidebar_color,
        color: dashboardTheme.sidebar_text_color,
        display: "flex",
        flexDirection: "column",
        transition: "width 0.25s ease",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          px: expanded ? 2 : 1,
          py: 2.5,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {expanded && (
          <Box
            onClick={() => goRoute("/admin-panel")}
            sx={{
              width: "100%",
              mb: 2.5,
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="FACTUSYSDR"
              sx={{
                width: "100%",
                maxWidth: 190,
                height: 78,
                objectFit: "contain",
                borderRadius: "18px",
                bgcolor: "rgba(255,255,255,0.96)",
                p: 1,
                boxShadow: "0 14px 32px rgba(0,0,0,0.18)",
              }}
            />
          </Box>
        )}

        <Stack spacing={1} sx={{ width: "100%", mt: expanded ? 0 : 1 }}>
          <Button
            fullWidth
            startIcon={<Inventory2RoundedIcon />}
            onClick={handleInventoryClick}
            sx={mainButtonStyles(isInventoryRoute)}
          >
            {expanded ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minWidth: 0,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Inventario
                </Typography>

                {inventoryOpen ? (
                  <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                ) : (
                  <KeyboardArrowRightRoundedIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                )}
              </Box>
            ) : (
              <Typography component="span" sx={shortTextStyles}>
                Invent.
              </Typography>
            )}
          </Button>

          {expanded && (
            <Collapse in={inventoryOpen} timeout="auto" unmountOnExit>
              <Stack spacing={0.7} sx={{ pt: 0.3 }}>
                {canViewProducts && (
                  <Button
                    fullWidth
                    onClick={() => goRoute("/admin-panel/productos")}
                    sx={nestedButtonStyles(isProductsRoute)}
                  >
                    Productos
                  </Button>
                )}
              </Stack>
            </Collapse>
          )}

          {canViewVentas && (
            <Button
              fullWidth
              startIcon={<ReceiptLongRoundedIcon />}
              onClick={() => goRoute("/admin-panel/ordenes")}
              sx={mainButtonStyles(isOrdersRoute)}
            >
              {expanded ? (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Órdenes
                </Typography>
              ) : (
                <Typography component="span" sx={shortTextStyles}>
                  Órdenes
                </Typography>
              )}
            </Button>
          )}

          {canViewVentas && (
            <Button
              fullWidth
              startIcon={<DescriptionRoundedIcon />}
              onClick={() => goRoute("/admin-panel/documentos")}
              sx={mainButtonStyles(isDocumentsRoute)}
            >
              {expanded ? (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Documentos
                </Typography>
              ) : (
                <Typography component="span" sx={shortTextStyles}>
                  Docs.
                </Typography>
              )}
            </Button>
          )}

          {canViewPersons && (
            <Button
              fullWidth
              startIcon={<PeopleAltRoundedIcon />}
              onClick={() => goRoute("/admin-panel/personas")}
              sx={mainButtonStyles(isPeopleRoute)}
            >
              {expanded ? (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Personas
                </Typography>
              ) : (
                <Typography component="span" sx={shortTextStyles}>
                  Personas
                </Typography>
              )}
            </Button>
          )}

          <Button
            fullWidth
            startIcon={<SettingsRoundedIcon />}
            onClick={handleConfigClick}
            sx={mainButtonStyles(isConfigRoute)}
          >
            {expanded ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  minWidth: 0,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  Configuración
                </Typography>

                {configOpen ? (
                  <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                ) : (
                  <KeyboardArrowRightRoundedIcon sx={{ fontSize: 20, flexShrink: 0 }} />
                )}
              </Box>
            ) : (
              <Typography component="span" sx={shortTextStyles}>
                Config.
              </Typography>
            )}
          </Button>

          {expanded && (
            <Collapse in={configOpen} timeout="auto" unmountOnExit>
              <Stack spacing={0.7} sx={{ pt: 0.3 }}>
                <Button
                  fullWidth
                  onClick={() => goTo("appearance")}
                  sx={nestedButtonStyles(isAppearanceActive)}
                >
                  Apariencia
                </Button>

                {canViewEnterprise && (
                  <Button
                    fullWidth
                    onClick={() => goTo("access")}
                    sx={nestedButtonStyles(isAccessActive)}
                  >
                    Accesos
                  </Button>
                )}
              </Stack>
            </Collapse>
          )}
        </Stack>
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            width: 260,
            height: "100dvh",
            border: "none",
            overflow: "hidden",
          },
        }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Box
      component="aside"
      sx={{
        width: sidebarWidth,
        flexShrink: 0,
        transition: "width 0.25s ease",
      }}
    >
      <Box
        sx={{
          width: sidebarWidth,
          height: "100dvh",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 30,
          transition: "width 0.25s ease",
        }}
      >
        {content}
      </Box>
    </Box>
  );
}